import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import TimezoneSelect from 'react-timezone-select';
import { TRAIN_FIELDS, validateTrainData, inferMissingFields, normalizeTrainData, useTrainFields, getTrainStationsList, getCityCodeFromCity, getTimezoneForCity } from '../trainUtils';
import 'react-datepicker/dist/react-datepicker.css';
import './AddTrainForm.css';
import { EnhancedSelect } from '@trip-planner-monorepo/shared';
import { useParams } from 'react-router-dom';
import { useAuth } from '@trip-planner-monorepo/shared';

const AddTrainForm = ({ onSubmit, initialData = {}, onCancel }) => {
    const { tripId } = useParams();
    const { currentUser } = useAuth();
    const trainFields = useTrainFields(tripId);

    // Define the list of standard cities in one place for reuse
    const STANDARD_CITIES = [
        { value: 'NYC', label: 'New York', name: 'New York' },
        { value: 'LON', label: 'London', name: 'London' },
        { value: 'TOK', label: 'Tokyo', name: 'Tokyo' },
        { value: 'PAR', label: 'Paris', name: 'Paris' },
        { value: 'BER', label: 'Berlin', name: 'Berlin' },
        { value: 'MAD', label: 'Madrid', name: 'Madrid' },
        { value: 'ROM', label: 'Rome', name: 'Rome' },
        { value: 'AMS', label: 'Amsterdam', name: 'Amsterdam' },
        { value: 'BRU', label: 'Brussels', name: 'Brussels' },
        { value: 'ZRH', label: 'Zurich', name: 'Zurich' },
        { value: 'VIE', label: 'Vienna', name: 'Vienna' },
        { value: 'CPH', label: 'Copenhagen', name: 'Copenhagen' },
        { value: 'OSL', label: 'Oslo', name: 'Oslo' },
        { value: 'STO', label: 'Stockholm', name: 'Stockholm' }
    ];

    // Convert date strings to Date objects when initializing from existing data
    const processedInitialData = {
        ...initialData,
        // Handle different field naming between storage and form
        departureDate: initialData.departureTime ? new Date(initialData.departureTime) : null,
        arrivalDate: initialData.arrivalTime ? new Date(initialData.arrivalTime) : null
    };

    // Ensure there's at least one passenger when initializing the form
    const initialPassengers = Array.isArray(processedInitialData.passengers) && 
        processedInitialData.passengers.length > 0 ? 
        processedInitialData.passengers : 
        [{
            firstName: '',
            lastName: '',
            seat: '',
            foodPreference: 'none'
        }];

    // Helper function to transform city strings to objects for the form
    const transformCityToObject = (cityName) => {
        if (!cityName) return '';
        
        // If it's already an object, return it as is
        if (typeof cityName === 'object') return cityName;
        
        // Try to find a match in standard cities
        const matchingCity = STANDARD_CITIES.find(city => 
            city.name === cityName || 
            city.label === cityName || 
            city.value === cityName
        );
        
        if (matchingCity) {
            return matchingCity;
        }
        
        // If no match, create a custom city object
        return {
            name: cityName,
            label: cityName,
            value: cityName.toLowerCase().replace(/\s+/g, '-'),
            isCustom: true
        };
    };

    // Helper function to transform station strings to objects for the form
    const transformStationToObject = (stationName) => {
        if (!stationName) return '';
        
        // If it's already an object, return it as is
        if (typeof stationName === 'object') return stationName;
        
        // Create a custom station object
        return {
            name: stationName,
            label: stationName,
            value: stationName,
            isCustom: true
        };
    };

    // State for form fields
    const [formData, setFormData] = useState({
        operator: processedInitialData.operator ? transformStationToObject(processedInitialData.operator) : '',
        trainNumber: processedInitialData.trainNumber || '',
        departureCity: processedInitialData.departureCity ? transformCityToObject(processedInitialData.departureCity) : '',
        departureStation: processedInitialData.departureStation ? transformStationToObject(processedInitialData.departureStation) : '',
        arrivalCity: processedInitialData.arrivalCity ? transformCityToObject(processedInitialData.arrivalCity) : '',
        arrivalStation: processedInitialData.arrivalStation ? transformStationToObject(processedInitialData.arrivalStation) : '',
        departureDate: processedInitialData.departureDate || null,
        arrivalDate: processedInitialData.arrivalDate || null,
        departureTime: processedInitialData.departureTime || null,
        arrivalTime: processedInitialData.arrivalTime || null,
        departureTimezone: processedInitialData.departureTimezone || '',
        arrivalTimezone: processedInitialData.arrivalTimezone || '',
        platform: processedInitialData.platform || '',
        coach: processedInitialData.coach || '',
        seat: processedInitialData.seat || '',
        classOfService: processedInitialData.classOfService || '',
        bookingReference: processedInitialData.bookingReference || '',
        bookingAgent: processedInitialData.bookingAgent || '',
        bookingLink: processedInitialData.bookingLink || '',
        notes: processedInitialData.notes || '',
        cost: processedInitialData.cost || {
            amount: '',
            currency: 'USD',
            paid: false
        },
        // Ensure arrays are properly initialized
        passengers: initialPassengers,
        ticketNumbers: processedInitialData.ticketNumbers || []
    });

    // State for errors and loading
    const [errors, setErrors] = useState({});
    const [trainTitle, setTrainTitle] = useState('');
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [operatorOptions, setOperatorOptions] = useState([]);
    const [departureStationOptions, setDepartureStationOptions] = useState([]);
    const [arrivalStationOptions, setArrivalStationOptions] = useState([]);
    const [ticketNumbers, setTicketNumbers] = useState(processedInitialData.ticketNumbers || []);
    const [newTicketNumber, setNewTicketNumber] = useState('');

    // Load operator options
    useEffect(() => {
        setLoadingOperators(true);
        try {
            const operators = TRAIN_FIELDS.operator.options();
            setOperatorOptions(operators);
        } catch (error) {
            console.error("Error loading train operators:", error);
        } finally {
            setLoadingOperators(false);
        }
    }, []);

    // Update departure station options when departure city changes
    useEffect(() => {
        try {
            // If a departure city is selected, filter stations by that city
            const stations = getTrainStationsList(formData.departureCity);
            setDepartureStationOptions(stations);
            
            // If current station doesn't belong to the new city, clear it
            if (formData.departureCity && formData.departureStation) {
                const stationBelongsToCity = stations.some(
                    station => station.code === (formData.departureStation.code || formData.departureStation.value || formData.departureStation)
                );
                
                if (!stationBelongsToCity) {
                    setFormData(prev => ({
                        ...prev,
                        departureStation: ''
                    }));
                }
            }
        } catch (error) {
            console.error("Error loading departure train stations:", error);
        }
    }, [formData.departureCity]);

    // Update arrival station options when arrival city changes
    useEffect(() => {
        try {
            // If an arrival city is selected, filter stations by that city
            const stations = getTrainStationsList(formData.arrivalCity);
            setArrivalStationOptions(stations);
            
            // If current station doesn't belong to the new city, clear it
            if (formData.arrivalCity && formData.arrivalStation) {
                const stationBelongsToCity = stations.some(
                    station => station.code === (formData.arrivalStation.code || formData.arrivalStation.value || formData.arrivalStation)
                );
                
                if (!stationBelongsToCity) {
                    setFormData(prev => ({
                        ...prev,
                        arrivalStation: ''
                    }));
                }
            }
        } catch (error) {
            console.error("Error loading arrival train stations:", error);
        }
    }, [formData.arrivalCity]);

    // Update departure timezone when departure city changes
    useEffect(() => {
        if (formData.departureCity) {
            const timezone = getTimezoneForCity(formData.departureCity);
            if (timezone && (!formData.departureTimezone || Object.keys(formData.departureTimezone).length === 0)) {
                setFormData(prev => ({
                    ...prev,
                    departureTimezone: timezone
                }));
            }
        }
    }, [formData.departureCity]);

    // Update arrival timezone when arrival city changes
    useEffect(() => {
        if (formData.arrivalCity) {
            const timezone = getTimezoneForCity(formData.arrivalCity);
            if (timezone && (!formData.arrivalTimezone || Object.keys(formData.arrivalTimezone).length === 0)) {
                setFormData(prev => ({
                    ...prev,
                    arrivalTimezone: timezone
                }));
            }
        }
    }, [formData.arrivalCity]);

    // Generate train title based on form data
    useEffect(() => {
        const generateTitle = () => {
            // Debug: check for objects that might cause rendering issues
            Object.entries(formData).forEach(([key, value]) => {
                if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
                    console.log(`Checking ${key} for potential object rendering issues:`, value);
                }
            });
            
            let title = '';
            
            // Helper function to safely extract string value from potential objects
            const getStringValue = (field) => {
                if (!field) return '';
                if (typeof field === 'string') return field;
                if (typeof field === 'object') {
                    // For timezone objects specifically
                    if (field.value && field.label && typeof field.value === 'string') {
                        return field.value;
                    }
                    // Try multiple possible properties where a string value might be stored
                    return field.name || field.label || field.value || '';
                }
                return String(field); // Convert any other type to string as fallback
            };
            
            // Add operator and train number if available
            const operatorString = getStringValue(formData.operator);
            if (operatorString) {
                title += operatorString;
            }
            
            if (formData.trainNumber) {
                if (title) title += ' ';
                title += formData.trainNumber;
            }
            
            // Add route if we have departure or arrival
            const departureStationString = getStringValue(formData.departureStation);
            const departureCityString = getStringValue(formData.departureCity);
            const arrivalStationString = getStringValue(formData.arrivalStation);
            const arrivalCityString = getStringValue(formData.arrivalCity);
            
            const hasDeparture = departureStationString || departureCityString;
            const hasArrival = arrivalStationString || arrivalCityString;
            
            if (hasDeparture || hasArrival) {
                if (title) title += ': ';
                
                if (hasDeparture) {
                    title += departureStationString || departureCityString;
                }
                
                if (hasDeparture && hasArrival) {
                    title += ' to ';
                }
                
                if (hasArrival) {
                    title += arrivalStationString || arrivalCityString;
                }
            }
            
            // Add date if available
            if (formData.departureDate) {
                const date = new Date(formData.departureDate);
                if (!isNaN(date.getTime())) {
                    title += ` (${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
                }
            }
            
            return title || 'New Train Journey';
        };
        
        setTrainTitle(generateTitle());
    }, [formData]);

    // Helper functions for passenger management
    // Check if a passenger has any data entered
    const hasPassengerData = (passenger) => {
        return passenger.firstName || 
               passenger.lastName || 
               passenger.seat || 
               (passenger.foodPreference && passenger.foodPreference !== 'none');
    };

    // Add a new passenger
    const addPassenger = () => {
        // Check if the last passenger has any data entered
        const lastPassengerIndex = formData.passengers.length - 1;
        const lastPassenger = formData.passengers[lastPassengerIndex];
        
        // Only allow adding if the last passenger has some data
        if (hasPassengerData(lastPassenger)) {
            setFormData(prev => ({
                ...prev,
                passengers: [...prev.passengers, {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    foodPreference: 'none'
                }]
            }));
        } else {
            // Optionally show a message that the current passenger needs data
            alert("Please enter information for the current passenger before adding another.");
        }
    };

    // Remove a passenger by index
    const removePassenger = (index) => {
        // If we're trying to remove the last passenger, just clear their fields
        if (formData.passengers.length === 1) {
            clearFirstPassenger();
            return;
        }
        
        // Otherwise, remove the passenger
        setFormData(prev => ({
            ...prev,
            passengers: prev.passengers.filter((_, i) => i !== index)
        }));
    };

    // Clear the first passenger's fields
    const clearFirstPassenger = () => {
        setFormData(prev => ({
            ...prev,
            passengers: [
                {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    foodPreference: 'none'
                },
                ...prev.passengers.slice(1) // Keep all other passengers
            ]
        }));
    };

    // Handle input changes
    const handleInputChange = (field, value) => {
        // Handle objects that come from select components
        const sanitizeValue = (val) => {
            // If it's not an object or null, return as is
            if (typeof val !== 'object' || val === null) return val;
            
            // If it's a select component value with label/value
            if (val.label !== undefined && val.value !== undefined) {
                return val; // Return as is for select components
            }

            // Deep copy to avoid modifying the original
            return JSON.parse(JSON.stringify(val));
        };

        // Special handling for nested fields like cost or passengers
        if (field.includes('.')) {
            const [parentField, childField] = field.split('.');
            
            if (parentField === 'cost') {
                setFormData(prev => ({
                    ...prev,
                    cost: {
                        ...prev.cost,
                        [childField]: sanitizeValue(value)
                    }
                }));
                return;
            }
            
            if (parentField === 'passengers') {
                const parts = field.split('.');
                const index = parseInt(parts[1]);
                const passengerField = parts[2];
                
                setFormData(prev => {
                    const newPassengers = [...prev.passengers];
                    if (!newPassengers[index]) {
                        newPassengers[index] = {};
                    }
                    newPassengers[index][passengerField] = sanitizeValue(value);
                    return { ...prev, passengers: newPassengers };
                });
                return;
            }
        }
        
        // Normal field handling
        setFormData(prev => ({
            ...prev,
            [field]: sanitizeValue(value)
        }));
        
        // Clear error when field is updated
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Handle date changes
    const handleDateChange = (field, date) => {
        console.log(`Date change for ${field}:`, date);
        
        // If date is null or undefined, clear the field
        if (!date) {
            setFormData(prev => ({
                ...prev,
                [field]: null
            }));
            return;
        }
        
        try {
            // Create a proper Date object
            const dateObj = new Date(date);
            
            // For departure/arrival, update both the date and time fields
            if (field === 'departureDate') {
                setFormData(prev => ({
                    ...prev,
                    departureDate: dateObj,
                    // Also update departureTime for consistent date handling
                    departureTime: dateObj.toISOString()
                }));
            } else if (field === 'arrivalDate') {
                setFormData(prev => ({
                    ...prev,
                    arrivalDate: dateObj,
                    // Also update arrivalTime for consistent date handling
                    arrivalTime: dateObj.toISOString()
                }));
            } else {
                // For other date fields
                setFormData(prev => ({
                    ...prev,
                    [field]: dateObj
                }));
            }
            
            // Check for date errors
            if (field === 'departureDate' || field === 'arrivalDate') {
                validateDates();
            }
        } catch (error) {
            console.error('Error setting date:', error);
        }
    };

    // Add this helper function to validate dates
    const validateDates = () => {
        const { departureDate, arrivalDate } = formData;
        
        if (departureDate && arrivalDate) {
            const depDate = new Date(departureDate);
            const arrDate = new Date(arrivalDate);
            
            if (depDate > arrDate) {
                setErrors(prev => ({
                    ...prev,
                    dates: 'Departure date cannot be after arrival date'
                }));
            } else {
                // Clear the error if it was previously set
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.dates;
                    return newErrors;
                });
            }
        }
    };

    // Handle ticket number addition
    const handleAddTicketNumber = () => {
        if (newTicketNumber.trim()) {
            setTicketNumbers([...ticketNumbers, newTicketNumber.trim()]);
            setNewTicketNumber('');
        }
    };

    // Handle ticket number removal
    const handleRemoveTicketNumber = (index) => {
        setTicketNumbers(ticketNumbers.filter((_, i) => i !== index));
    };

    // Get field error if any
    const getFieldError = (fieldName) => {
        return errors[fieldName] || null;
    };

    // Render field error message
    const renderFieldError = (fieldName) => {
        const error = getFieldError(fieldName);
        return error && <div className="error-message">{error}</div>;
    };

    // Render form field
    const renderField = (fieldName, label, component) => {
        const isRequired = TRAIN_FIELDS[fieldName]?.required;

        return (
            <div className="form-group">
                <label htmlFor={fieldName}>
                    {label}
                    {isRequired && <span className="required">*</span>}
                </label>
                {component}
                {renderFieldError(fieldName)}
            </div>
        );
    };

    // Handle cost checkbox
    const handlePaidChange = (e) => {
        setFormData(prev => ({
            ...prev,
            cost: {
                ...prev.cost,
                paid: e.target.checked
            }
        }));
    };

    // Helper function to safely extract string values for display
    const getDisplayValue = (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            // Handle timezone objects
            if (value.label && value.value) {
                return value.label;
            }
            // Handle other objects
            return value.name || value.label || value.value || '';
        }
        return String(value);
    };

    // Submit the form
    const handleSubmit = (e) => {
        e.preventDefault();
        
        try {
            console.log("Attempting to submit train form with data:", formData);
            
            // Helper function to safely extract string value from potential objects (same as in generateTitle)
            const getStringValue = (field) => {
                if (!field) return '';
                if (typeof field === 'string') return field;
                if (typeof field === 'object') {
                    // For timezone objects specifically
                    if (field.value && field.label && typeof field.value === 'string') {
                        return field.value;
                    }
                    // Try multiple possible properties where a string value might be stored
                    return field.name || field.label || field.value || '';
                }
                return String(field); // Convert any other type to string as fallback
            };
            
            // Check if we have enough info to create a title - same approach as flight form
            const hasTitleInfo = 
                getStringValue(formData.operator) || 
                formData.trainNumber || 
                getStringValue(formData.departureCity) || 
                getStringValue(formData.arrivalCity) || 
                getStringValue(formData.departureStation) || 
                getStringValue(formData.arrivalStation);
                
            if (!hasTitleInfo) {
                setErrors({
                    form: 'Please provide some train information before saving. At minimum, include an operator, train number, or departure/arrival location.'
                });
                return;
            }
            
            // Create a deep copy to avoid modifying state
            const normalizedData = JSON.parse(JSON.stringify(formData));
            
            // Process city and station data from EnhancedSelect using our helper function
            normalizedData.departureCity = getStringValue(normalizedData.departureCity);
            normalizedData.arrivalCity = getStringValue(normalizedData.arrivalCity);
            normalizedData.departureStation = getStringValue(normalizedData.departureStation);
            normalizedData.arrivalStation = getStringValue(normalizedData.arrivalStation);
            normalizedData.operator = getStringValue(normalizedData.operator);
            
            // Process timezone data specifically
            if (normalizedData.departureTimezone && typeof normalizedData.departureTimezone === 'object') {
                normalizedData.departureTimezone = getStringValue(normalizedData.departureTimezone);
            }
            
            if (normalizedData.arrivalTimezone && typeof normalizedData.arrivalTimezone === 'object') {
                normalizedData.arrivalTimezone = getStringValue(normalizedData.arrivalTimezone);
            }
            
            // Convert dates to ISO strings if they exist
            if (normalizedData.departureDate instanceof Date) {
                normalizedData.departureTime = normalizedData.departureDate.toISOString();
            } else if (normalizedData.departureDate) {
                // If not a Date object but has a value, try to convert it
                try {
                    normalizedData.departureTime = new Date(normalizedData.departureDate).toISOString();
                } catch (e) {
                    console.error('Error converting departureDate to ISO string:', e);
                }
            }
            
            if (normalizedData.arrivalDate instanceof Date) {
                normalizedData.arrivalTime = normalizedData.arrivalDate.toISOString();
            } else if (normalizedData.arrivalDate) {
                // If not a Date object but has a value, try to convert it
                try {
                    normalizedData.arrivalTime = new Date(normalizedData.arrivalDate).toISOString();
                } catch (e) {
                    console.error('Error converting arrivalDate to ISO string:', e);
                }
            }
            
            // Add ticketNumbers to the submission
            normalizedData.ticketNumbers = ticketNumbers.filter(t => t && t.trim() !== '');
            
            // Clean up passengers data
            if (Array.isArray(normalizedData.passengers)) {
                // Remove empty passengers
                normalizedData.passengers = normalizedData.passengers.filter(p => 
                    p.firstName || p.lastName || p.seat || 
                    (p.foodPreference && p.foodPreference !== 'none')
                );
                
                // If no passengers, set to empty array
                if (normalizedData.passengers.length === 0) {
                    normalizedData.passengers = [];
                }
            }
            
            // Prepare travel data with train details
            const travelData = {
                type: 'train',
                details: {
                    operator: normalizedData.operator || "",
                    trainNumber: normalizedData.trainNumber || "",
                    departureCity: normalizedData.departureCity || "",
                    departureStation: normalizedData.departureStation || "",
                    arrivalCity: normalizedData.arrivalCity || "",
                    arrivalStation: normalizedData.arrivalStation || "",
                    departureTime: normalizedData.departureTime || null,
                    arrivalTime: normalizedData.arrivalTime || null,
                    departureTimezone: normalizedData.departureTimezone || "",
                    arrivalTimezone: normalizedData.arrivalTimezone || "",
                    platform: normalizedData.platform || "",
                    coach: normalizedData.coach || "",
                    seat: normalizedData.seat || "",
                    bookingAgent: normalizedData.bookingAgent || "",
                    bookingReference: normalizedData.bookingReference || "",
                    bookingLink: normalizedData.bookingLink || "",
                    ticketNumbers: normalizedData.ticketNumbers || [],
                    passengers: normalizedData.passengers || [],
                    classOfService: normalizedData.classOfService || "",
                    notes: normalizedData.notes || ""
                },
                status: initialData.status || 'Planned',
                summary: trainTitle,
                startDate: normalizedData.departureTime || null,
                endDate: normalizedData.arrivalTime || null,
                cost: normalizedData.cost || {
                    amount: 0,
                    currency: 'USD',
                    paid: false
                }
            };

            console.log('Prepared train travel data:', travelData);
            onSubmit(travelData);
        } catch (error) {
            setErrors({
                form: 'Failed to save train details. Please try again.'
            });
            console.error('Error saving train:', error);
        }
    };

    return (
        <div className="train-form">
            <div className="train-title">
                <h1>Train Details</h1>
                {trainTitle ? (
                    <div className="train-title-display">{trainTitle}</div>
                ) : (
                    <div className="train-title-placeholder">
                        A title for this train journey will be displayed here as you enter values in the fields below.
                    </div>
                )}
            </div>

            {errors.form && <div className="error-message">{errors.form}</div>}

            <form onSubmit={handleSubmit}>
                {/* Train Identification Section */}
                <div className="form-section">
                    <h2 className="section-title">Train Identification</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="operator">Train Operator:</label>
                            <EnhancedSelect
                                options={operatorOptions}
                                onChange={(selected) => handleInputChange('operator', selected)}
                                value={formData.operator}
                                placeholder={loadingOperators ? "Loading operators..." : "Select or type any train operator"}
                                isSearchable={true}
                                isLoading={loadingOperators}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any operator name and press Enter
                            </small>
                            {formData.operator && typeof formData.operator === 'object' && formData.operator.isCustom && (
                                <small className="custom-input-note">Using custom operator name: "{typeof formData.operator.name === 'string' ? formData.operator.name : formData.operator.label}"</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="trainNumber">Train Number/Service:</label>
                            <input
                                type="text"
                                id="trainNumber"
                                className="form-control"
                                value={formData.trainNumber}
                                onChange={(e) => handleInputChange('trainNumber', e.target.value)}
                                placeholder="e.g. IC 2345, TGV 6215"
                            />
                        </div>
                    </div>
                </div>

                {/* Departure Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Departure Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureCity">
                                Departure City:
                            </label>
                            <EnhancedSelect
                                options={STANDARD_CITIES}
                                onChange={(selected) => handleInputChange('departureCity', selected)}
                                value={formData.departureCity}
                                placeholder="Select or type any city"
                                isSearchable={true}
                                allowCustomValues={true}
                                className="form-control"
                            />
                            {formData.departureCity && typeof formData.departureCity === 'object' && formData.departureCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{typeof formData.departureCity.name === 'string' ? formData.departureCity.name : formData.departureCity.label}"</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="departureStation">
                                Departure Station:
                            </label>
                            <EnhancedSelect
                                options={departureStationOptions}
                                onChange={(selected) => handleInputChange('departureStation', selected)}
                                value={formData.departureStation}
                                placeholder={formData.departureCity ? "Select station" : "Select a city first"}
                                isSearchable={true}
                                allowCustomValues={true}
                                className="form-control"
                                isDisabled={!formData.departureCity}
                            />
                            {formData.departureStation && typeof formData.departureStation === 'object' && formData.departureStation.isCustom && (
                                <small className="custom-input-note">Using custom station: "{typeof formData.departureStation.name === 'string' ? formData.departureStation.name : formData.departureStation.label}"</small>
                            )}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureDate">
                                Departure Date & Time:
                            </label>
                            <DatePicker
                                selected={formData.departureDate}
                                onChange={(date) => handleDateChange('departureDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                                placeholderText="Select departure date and time"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="departureTimezone">
                                Departure Timezone:
                            </label>
                            <TimezoneSelect
                                value={formData.departureTimezone}
                                onChange={(selected) => handleInputChange('departureTimezone', selected)}
                                className="timezone-select form-control"
                            />
                            {formData.departureTimezone && typeof formData.departureTimezone === 'object' && 
                             <small className="timezone-debug" style={{display: 'none'}}>
                                Timezone value: {getDisplayValue(formData.departureTimezone)}
                             </small>
                            }
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="platform">
                                Platform:
                            </label>
                            <input
                                type="text"
                                id="platform"
                                className="form-control"
                                value={formData.platform}
                                onChange={(e) => handleInputChange('platform', e.target.value)}
                                placeholder="e.g. 5A"
                            />
                        </div>
                    </div>
                </div>

                {/* Arrival Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Arrival Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalCity">
                                Arrival City:
                            </label>
                            <EnhancedSelect
                                options={STANDARD_CITIES}
                                onChange={(selected) => handleInputChange('arrivalCity', selected)}
                                value={formData.arrivalCity}
                                placeholder="Select or type any city"
                                isSearchable={true}
                                allowCustomValues={true}
                                className="form-control"
                            />
                            {formData.arrivalCity && typeof formData.arrivalCity === 'object' && formData.arrivalCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{typeof formData.arrivalCity.name === 'string' ? formData.arrivalCity.name : formData.arrivalCity.label}"</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="arrivalStation">
                                Arrival Station:
                            </label>
                            <EnhancedSelect
                                options={arrivalStationOptions}
                                onChange={(selected) => handleInputChange('arrivalStation', selected)}
                                value={formData.arrivalStation}
                                placeholder={formData.arrivalCity ? "Select station" : "Select a city first"}
                                isSearchable={true}
                                allowCustomValues={true}
                                className="form-control"
                                isDisabled={!formData.arrivalCity}
                            />
                            {formData.arrivalStation && typeof formData.arrivalStation === 'object' && formData.arrivalStation.isCustom && (
                                <small className="custom-input-note">Using custom station: "{typeof formData.arrivalStation.name === 'string' ? formData.arrivalStation.name : formData.arrivalStation.label}"</small>
                            )}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalDate">
                                Arrival Date & Time:
                            </label>
                            <DatePicker
                                selected={formData.arrivalDate}
                                onChange={(date) => handleDateChange('arrivalDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                                placeholderText="Select arrival date and time"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="arrivalTimezone">
                                Arrival Timezone:
                            </label>
                            <TimezoneSelect
                                value={formData.arrivalTimezone}
                                onChange={(selected) => handleInputChange('arrivalTimezone', selected)}
                                className="timezone-select form-control"
                            />
                            {formData.arrivalTimezone && typeof formData.arrivalTimezone === 'object' && 
                             <small className="timezone-debug" style={{display: 'none'}}>
                                Timezone value: {getDisplayValue(formData.arrivalTimezone)}
                             </small>
                            }
                        </div>
                    </div>
                </div>

                {/* Booking Details Section */}
                <div className="form-section">
                    <h2 className="section-title">Booking Details</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="bookingAgent">Booking Agent:</label>
                            <input
                                type="text"
                                id="bookingAgent"
                                className="form-control"
                                value={formData.bookingAgent}
                                onChange={(e) => handleInputChange('bookingAgent', e.target.value)}
                                placeholder="Enter booking agent (e.g., Trainline, Rail Europe)"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bookingReference">Booking Reference:</label>
                            <input
                                type="text"
                                id="bookingReference"
                                className="form-control"
                                value={formData.bookingReference}
                                onChange={(e) => handleInputChange('bookingReference', e.target.value)}
                                placeholder="Enter booking reference code"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="bookingLink">Booking Link:</label>
                            <input
                                type="text"
                                id="bookingLink"
                                className="form-control"
                                value={formData.bookingLink}
                                onChange={(e) => handleInputChange('bookingLink', e.target.value)}
                                placeholder="Enter booking website URL"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="cost.amount">Cost:</label>
                            <div className="cost-input-group">
                                <select
                                    id="cost.currency"
                                    className="currency-select form-control"
                                    value={formData.cost.currency}
                                    onChange={(e) => handleInputChange('cost.currency', e.target.value)}
                                >
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="GBP">GBP</option>
                                    <option value="JPY">JPY</option>
                                    <option value="CAD">CAD</option>
                                    <option value="AUD">AUD</option>
                                    <option value="CHF">CHF</option>
                                    <option value="CNY">CNY</option>
                                </select>
                                <input
                                    type="number"
                                    id="cost.amount"
                                    className="cost-amount form-control"
                                    value={formData.cost.amount}
                                    onChange={(e) => handleInputChange('cost.amount', e.target.value)}
                                    placeholder="Enter amount"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="paid-checkbox-container">
                                <label htmlFor="paid" className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        id="paid"
                                        checked={formData.cost.paid}
                                        onChange={handlePaidChange}
                                    />
                                    <span className="checkbox-text">Paid</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group ticket-numbers-container">
                            <label>Ticket Number(s):</label>
                            <div className="ticket-numbers-list">
                                {ticketNumbers.map((ticket, index) => (
                                    <div key={index} className="ticket-number-item">
                                        <span>{ticket}</span>
                                        <button
                                            type="button"
                                            className="ticket-remove-btn"
                                            onClick={() => handleRemoveTicketNumber(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="ticket-number-add">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={newTicketNumber}
                                    onChange={(e) => setNewTicketNumber(e.target.value)}
                                    placeholder="Enter ticket number"
                                />
                                <button
                                    type="button"
                                    className="ticket-add-btn"
                                    onClick={handleAddTicketNumber}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Passenger Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Passenger Information</h2>
                    {formData.passengers.map((passenger, index) => (
                        <div key={index} className="passenger-container">
                            <h3 className="passenger-title">Passenger {index + 1}</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.firstName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.firstName`, e.target.value)}
                                        placeholder="Enter first name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.lastName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.lastName`, e.target.value)}
                                        placeholder="Enter last name"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Seat:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={passenger.seat || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.seat`, e.target.value)}
                                        placeholder="e.g., 32A, Window"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Food Preference:</label>
                                    <select
                                        className="form-control"
                                        value={passenger.foodPreference || 'none'}
                                        onChange={(e) => handleInputChange(`passengers.${index}.foodPreference`, e.target.value)}
                                    >
                                        <option value="none">None</option>
                                        <option value="vegetarian">Vegetarian</option>
                                        <option value="vegan">Vegan</option>
                                        <option value="gluten-free">Gluten-Free</option>
                                        <option value="kosher">Kosher</option>
                                        <option value="halal">Halal</option>
                                    </select>
                                </div>
                            </div>
                            {/* Buttons for this passenger */}
                            <div className="passenger-buttons">
                                {formData.passengers.length === 1 && hasPassengerData(passenger) ? (
                                    <button
                                        type="button"
                                        className="clear-button"
                                        onClick={clearFirstPassenger}
                                    >
                                        Clear Fields
                                    </button>
                                ) : formData.passengers.length > 1 ? (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removePassenger(index)}
                                    >
                                        Remove Passenger
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    {/* Add passenger button */}
                    <div className="add-button-container">
                        <button
                            type="button"
                            className="add-button"
                            onClick={addPassenger}
                        >
                            + Add Another Passenger
                        </button>
                    </div>
                </div>

                {/* Additional Information Section - Keep Seat Information and update */}
                <div className="form-section">
                    <h2 className="section-title">Additional Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="coach">
                                Coach/Car Number:
                            </label>
                            <input
                                type="text"
                                id="coach"
                                className="form-control"
                                value={formData.coach}
                                onChange={(e) => handleInputChange('coach', e.target.value)}
                                placeholder="e.g. 7"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="classOfService">
                                Class of Service:
                            </label>
                            <select
                                id="classOfService"
                                value={formData.classOfService}
                                onChange={(e) => handleInputChange('classOfService', e.target.value)}
                                className="form-control"
                            >
                                <option value="">Select class...</option>
                                <option value="first">First Class</option>
                                <option value="second">Second Class</option>
                                <option value="business">Business</option>
                                <option value="economy">Economy</option>
                                <option value="sleeper">Sleeper</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <label htmlFor="notes">
                                Notes:
                            </label>
                            <textarea
                                id="notes"
                                className="form-control"
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                placeholder="Any additional information about this train journey"
                                rows={4}
                            />
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                    <button type="submit" className="save-button">
                        Save Train Details
                    </button>
                    <button type="button" onClick={onCancel} className="cancel-button">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddTrainForm; 